import React, { useState, useRef, useCallback } from 'react';
import { importApi } from '../services/api';
import { ImportStatus, ImportReport } from '../types';
import './WordPressImporter.css';

interface WordPressImporterProps {
  onImportComplete?: (report: ImportReport) => void;
}

type ImportState = 'idle' | 'validating' | 'importing' | 'completed' | 'error';

const WordPressImporter: React.FC<WordPressImporterProps> = ({ onImportComplete }) => {
  const [state, setState] = useState<ImportState>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [_importId, setImportId] = useState<string | null>(null);
  const [status, setStatus] = useState<ImportStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Limpar intervalo de polling ao desmontar
  React.useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const handleFileSelect = useCallback((file: File) => {
    // Validar tipo de arquivo
    if (!file.name.endsWith('.xml')) {
      setError('Por favor, selecione um arquivo XML válido.');
      return;
    }

    setSelectedFile(file);
    setError(null);
    setState('idle');
    setStatus(null);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const pollStatus = useCallback(async (id: string) => {
    try {
      const statusData = await importApi.getStatus(id);
      setStatus(statusData);

      if (statusData.status === 'completed' || statusData.status === 'error') {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setState(statusData.status === 'completed' ? 'completed' : 'error');
        
        if (statusData.status === 'completed' && statusData.report && onImportComplete) {
          onImportComplete(statusData.report);
        }
      }
    } catch (err) {
      console.error('Erro ao verificar status:', err);
    }
  }, [onImportComplete]);

  const startImport = async () => {
    if (!selectedFile) return;

    setState('importing');
    setError(null);

    try {
      const result = await importApi.importWordPress(selectedFile);
      setImportId(result.importId || null);
      setStatus(result);

      // Se a importação é assíncrona, iniciar polling
      if (result.importId) {
        pollIntervalRef.current = setInterval(() => {
          pollStatus(result.importId!);
        }, 2000);
      } else if (result.status === 'completed') {
        setState('completed');
        if (result.report && onImportComplete) {
          onImportComplete(result.report);
        }
      }
    } catch (err: any) {
      setState('error');
      setError(err.message || 'Erro ao iniciar importação');
    }
  };

  const resetImporter = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setState('idle');
    setSelectedFile(null);
    setImportId(null);
    setStatus(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className="wordpress-importer">
      <div className="importer-header">
        <h2>Importar do WordPress</h2>
        <p className="importer-description">
          Importe livros de um arquivo de exportação XML do WordPress.
          O sistema irá extrair títulos, descrições, imagens e categorias automaticamente.
        </p>
      </div>

      {/* Área de upload */}
      {state === 'idle' && (
        <div
          className={`upload-area ${dragActive ? 'drag-active' : ''} ${selectedFile ? 'has-file' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleBrowseClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xml"
            onChange={handleInputChange}
            className="file-input"
          />
          
          {selectedFile ? (
            <div className="selected-file">
              <span className="file-icon">📄</span>
              <div className="file-info">
                <span className="file-name">{selectedFile.name}</span>
                <span className="file-size">{formatFileSize(selectedFile.size)}</span>
              </div>
              <button
                className="remove-file"
                onClick={(e) => {
                  e.stopPropagation();
                  resetImporter();
                }}
              >
                ✕
              </button>
            </div>
          ) : (
            <div className="upload-prompt">
              <span className="upload-icon">📁</span>
              <p>Arraste um arquivo XML aqui ou clique para selecionar</p>
              <span className="upload-hint">Arquivo de exportação do WordPress (.xml)</span>
            </div>
          )}
        </div>
      )}

      {/* Barra de progresso */}
      {(state === 'validating' || state === 'importing') && (
        <div className="progress-section">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${status?.progress || 0}%` }}
            />
          </div>
          <p className="progress-message">{status?.message || 'Processando...'}</p>
        </div>
      )}

      {/* Relatório de importação */}
      {state === 'completed' && status?.report && (
        <div className="import-report">
          <div className="report-header success">
            <span className="report-icon">✓</span>
            <h3>Importação Concluída</h3>
          </div>
          
          <div className="report-stats">
            <div className="stat">
              <span className="stat-value">{status.report.booksImported}</span>
              <span className="stat-label">Livros importados</span>
            </div>
            <div className="stat">
              <span className="stat-value">{status.report.imagesDownloaded}</span>
              <span className="stat-label">Imagens baixadas</span>
            </div>
            <div className="stat">
              <span className="stat-value">{status.report.tagsCreated}</span>
              <span className="stat-label">Tags criadas</span>
            </div>
            <div className="stat">
              <span className="stat-value">{formatDuration(status.report.duration)}</span>
              <span className="stat-label">Tempo total</span>
            </div>
          </div>

          {status.report.warnings && status.report.warnings.length > 0 && (
            <div className="report-warnings">
              <h4>Avisos ({status.report.warnings.length})</h4>
              <ul>
                {status.report.warnings.slice(0, 5).map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
                {status.report.warnings.length > 5 && (
                  <li className="more-warnings">
                    ... e mais {status.report.warnings.length - 5} avisos
                  </li>
                )}
              </ul>
            </div>
          )}

          {status.report.errors && status.report.errors.length > 0 && (
            <div className="report-errors">
              <h4>Erros ({status.report.errors.length})</h4>
              <ul>
                {status.report.errors.slice(0, 5).map((err, index) => (
                  <li key={index}>
                    <strong>{err.item}:</strong> {err.error}
                  </li>
                ))}
                {status.report.errors.length > 5 && (
                  <li className="more-errors">
                    ... e mais {status.report.errors.length - 5} erros
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Mensagem de erro */}
      {state === 'error' && (
        <div className="import-error">
          <div className="error-header">
            <span className="error-icon">✕</span>
            <h3>Erro na Importação</h3>
          </div>
          <p className="error-message">{error || status?.message || 'Ocorreu um erro durante a importação.'}</p>
          
          {status?.report?.errors && status.report.errors.length > 0 && (
            <div className="error-details">
              <h4>Detalhes dos erros:</h4>
              <ul>
                {status.report.errors.map((err, index) => (
                  <li key={index}>
                    <strong>{err.item}:</strong> {err.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Botões de ação */}
      <div className="importer-actions">
        {state === 'idle' && selectedFile && (
          <button className="btn-primary" onClick={startImport}>
            Iniciar Importação
          </button>
        )}
        
        {(state === 'completed' || state === 'error') && (
          <button className="btn-secondary" onClick={resetImporter}>
            Nova Importação
          </button>
        )}
        
        {(state === 'validating' || state === 'importing') && (
          <button className="btn-secondary" disabled>
            Processando...
          </button>
        )}
      </div>

      {/* Instruções */}
      <div className="importer-instructions">
        <h4>Como exportar do WordPress:</h4>
        <ol>
          <li>Acesse o painel administrativo do WordPress</li>
          <li>Vá em Ferramentas → Exportar</li>
          <li>Selecione "Posts" ou "Todo o conteúdo"</li>
          <li>Clique em "Baixar arquivo de exportação"</li>
          <li>Faça upload do arquivo XML aqui</li>
        </ol>
      </div>
    </div>
  );
};

export default WordPressImporter;
